import * as XLSX from 'xlsx';
import { CUKCUK_PARSER_VERSION } from '../constants/cukcuk';
import type { Customization } from '../types/customization';
import type { Dish } from '../types/dish';
import type { CukcukInvoiceParseMeta, MenuOption } from '../types/cukcukImport';
import type { InvoiceNotification } from '../types/invoiceNotification';
import { getSheetMatrix, readCukcukWorkbookData } from './cukcukXlsxWorkerClient';
import {
  groupInvoiceRowsByCode,
  isCukcukInvoiceFile,
  resolveCukcukInvoiceDataBounds,
} from './cukcukParser';
import {
  mapInvoiceOptions,
  type MappedCukcukInvoiceLineItem,
} from './invoiceOptionMapper';
import { pickSheetName } from './xlsxUtils';

const PARSER_VERSION = CUKCUK_PARSER_VERSION;

const INVOICE_SHEET_HINTS = [
  'chitietdoanhthu',
  'doanhthu',
  'hoadon',
  'sheet1',
];

export interface MappedCukcukInvoice {
  invoiceCode: string;
  saleDate?: string;
  checkInTime?: string;
  checkOutTime?: string;
  totalAmount?: number;
  merchandiseAmount?: number;
  feeAmount?: number;
  vatAmount?: number;
  discountAmount?: number;
  guestCount?: number;
  customerName?: string;
  serviceMode?: string;
  area?: string;
  tableName?: string;
  shiftName?: string;
  cashierName?: string;
  serverName?: string;
  items: MappedCukcukInvoiceLineItem[];
}

const applyInvoiceSummary = (invoice: MappedCukcukInvoice, row: unknown[]): void => {
  const parseExcelDate = (value: unknown): string | undefined => {
    if (value === undefined || value === null || value === '') return undefined;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const day = String(value.getDate()).padStart(2, '0');
      const month = String(value.getMonth() + 1).padStart(2, '0');
      return `${day}/${month}/${value.getFullYear()}`;
    }

    if (typeof value === 'number') {
      const parsed = XLSX.SSF.parse_date_code(value);
      if (!parsed) return String(value);
      const day = String(parsed.d).padStart(2, '0');
      const month = String(parsed.m).padStart(2, '0');
      return `${day}/${month}/${parsed.y}`;
    }

    return String(value).trim() || undefined;
  };

  const parseTimeRange = (value: unknown): { checkIn?: string; checkOut?: string } => {
    const text = String(value ?? '').trim();
    if (!text) return {};

    const parts = text.split('-').map((part) => part.trim()).filter(Boolean);
    if (!parts.length) return {};

    return {
      checkIn: parts[0],
      checkOut: parts[1] ?? parts[0],
    };
  };

  const toNumber = (value: unknown): number => {
    if (value === undefined || value === null || value === '') return 0;
    const normalized = String(value).replace(/[,\s]/g, '').replace(/[^\d.-]/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const INV = {
    SALE_DATE: 0,
    TIME_RANGE: 1,
    LINE_TOTAL: 8,
    MERCHANDISE_AMOUNT: 9,
    FEE_AMOUNT: 10,
    VAT_AMOUNT: 11,
    DISCOUNT_AMOUNT: 12,
    GUEST_COUNT: 19,
    CUSTOMER_NAME: 18,
    SERVICE_MODE: 20,
    AREA: 21,
    TABLE_NAME: 22,
    SHIFT_NAME: 23,
    CASHIER_NAME: 24,
    SERVER_NAME: 25,
  } as const;

  const times = parseTimeRange(row[INV.TIME_RANGE]);

  invoice.saleDate = parseExcelDate(row[INV.SALE_DATE]);
  invoice.checkInTime = times.checkIn;
  invoice.checkOutTime = times.checkOut;
  invoice.totalAmount = toNumber(row[INV.LINE_TOTAL]) || undefined;
  invoice.merchandiseAmount = toNumber(row[INV.MERCHANDISE_AMOUNT]) || undefined;
  invoice.feeAmount = toNumber(row[INV.FEE_AMOUNT]) || undefined;
  invoice.vatAmount = toNumber(row[INV.VAT_AMOUNT]) || undefined;
  invoice.discountAmount = toNumber(row[INV.DISCOUNT_AMOUNT]) || undefined;

  const guestCount = toNumber(row[INV.GUEST_COUNT]);
  if (guestCount > 0) invoice.guestCount = guestCount;

  const customerName = String(row[INV.CUSTOMER_NAME] ?? '').trim();
  if (customerName) invoice.customerName = customerName;

  const serviceMode = String(row[INV.SERVICE_MODE] ?? '').trim();
  if (serviceMode) invoice.serviceMode = serviceMode;

  const area = String(row[INV.AREA] ?? '').trim();
  if (area) invoice.area = area;

  const tableName = String(row[INV.TABLE_NAME] ?? '').trim();
  if (tableName) invoice.tableName = tableName;

  const shiftName = String(row[INV.SHIFT_NAME] ?? '').trim();
  if (shiftName) invoice.shiftName = shiftName;

  const cashierName = String(row[INV.CASHIER_NAME] ?? '').trim();
  if (cashierName) invoice.cashierName = cashierName;

  const serverName = String(row[INV.SERVER_NAME] ?? '').trim();
  if (serverName) invoice.serverName = serverName;
};

export interface ParseCukcukInvoicesWithMappingResult {
  invoices: MappedCukcukInvoice[];
  meta: CukcukInvoiceParseMeta;
}

const buildNotificationContent = (invoice: InvoiceNotification): string => {
  const parts: string[] = [];

  if (invoice.tableName) {
    parts.push(`Bàn: ${invoice.tableName}`);
  }
  if (invoice.itemName) {
    parts.push(`Món: ${invoice.itemName}`);
  }
  if (invoice.quantity !== undefined) {
    parts.push(`SL: ${invoice.quantity}`);
  }
  if (invoice.lineTotal !== undefined) {
    parts.push(`Thành tiền: ${invoice.lineTotal.toLocaleString('vi-VN')} ${invoice.currency || 'VND'}`);
  }
  if (invoice.optionSummary) {
    parts.push(`Tùy chọn: ${invoice.optionSummary}`);
  }

  return parts.join(' | ');
};

export interface OptionCatalogSources {
  /** Catalog từ Firebase `8am_customization` — ưu tiên cao nhất. */
  customizations?: Customization[];
  /** Catalog từ file menu CUKCUK — bổ sung khi customization chưa có mã. */
  menuOptions?: MenuOption[];
  /** Món ảo / option trong `8am_dishes`. */
  dishes?: Dish[];
}

/** Xây catalog option từ nhóm tùy chỉnh Firebase để dùng cho `mapCukcukOption`. */
export const buildMenuOptionCatalogFromCustomizations = (
  customizations: Customization[],
): MenuOption[] => {
  const seen = new Map<string, MenuOption>();

  customizations.forEach((customization) => {
    (customization.groups || []).forEach((group) => {
      (group.dishCodes || []).forEach((dish) => {
        const code = dish.code?.trim();
        const name = dish.name?.trim();
        if (!code || !name || dish.isActive === false || seen.has(code)) return;

        seen.set(code, {
          code,
          name,
          price: Number(dish.price || 0),
          isActive: true,
          optionGroupName: group.groupName,
          source: {
            cukcukCode: code,
            cukcukName: name,
            cukcukGroupName: group.groupName,
          },
        });
      });
    });
  });

  return Array.from(seen.values());
};

/** Catalog từ món ảo (option) trong `8am_dishes`. */
export const buildMenuOptionCatalogFromDishes = (dishes: Dish[]): MenuOption[] => {
  const seen = new Map<string, MenuOption>();

  dishes.forEach((dish) => {
    if (!dish.isVirtualDish) return;

    const code = dish.code?.trim();
    const name = dish.name?.trim();
    if (!code || !name || dish.isActive === false || seen.has(code)) return;

    seen.set(code, {
      code,
      name,
      price: Number(dish.price || 0),
      isActive: true,
      optionGroupName: dish.groupName || dish.group,
      source: {
        cukcukCode: code,
        cukcukName: name,
        cukcukGroupName: dish.groupName || dish.group,
      },
    });
  });

  return Array.from(seen.values());
};

/**
 * Gộp catalog option từ nhiều nguồn.
 * Customization (admin) thắng khi trùng mã; menu/dishes CUKCUK lấp chỗ trống.
 */
export const resolveOptionCatalog = (sources: OptionCatalogSources): MenuOption[] => {
  const seen = new Map<string, MenuOption>();

  buildMenuOptionCatalogFromCustomizations(sources.customizations ?? []).forEach((option) => {
    seen.set(option.code, option);
  });

  buildMenuOptionCatalogFromDishes(sources.dishes ?? []).forEach((option) => {
    if (!seen.has(option.code)) {
      seen.set(option.code, option);
    }
  });

  (sources.menuOptions ?? []).forEach((option) => {
    if (!seen.has(option.code)) {
      seen.set(option.code, option);
    }
  });

  return Array.from(seen.values());
};

const stableLinePart = (value: unknown): string =>
  encodeURIComponent(String(value ?? '').trim().toLowerCase());

const buildStableLineFingerprint = (item: MappedCukcukInvoiceLineItem): string => stableLinePart(JSON.stringify([
  item.itemId ?? '', item.itemName ?? '', item.quantity ?? null, item.unit ?? '', item.unitPrice ?? null,
  item.lineTotal ?? null, item.vatRate ?? '', item.selectedOptions.map((option) => [
    option.optionId ?? '', option.optionName ?? '', option.quantity ?? null,
  ]).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
]));

/** Chuyển hóa đơn CUKCUK đã map option sang `InvoiceNotification` để lưu Firebase. */
export const cukcukInvoicesToNotifications = (
  invoices: MappedCukcukInvoice[],
): InvoiceNotification[] => {
  const result: InvoiceNotification[] = [];

  invoices.forEach((invoice) => {
    const occurrences = new Map<string, number>();
    invoice.items.forEach((item) => {
      const fingerprint = buildStableLineFingerprint(item);
      const occurrence = (occurrences.get(fingerprint) ?? 0) + 1;
      occurrences.set(fingerprint, occurrence);
      const selectedOptions = (item.selectedOptions || []).map((option) => ({
        optionId: option.optionId,
        optionName: option.optionName,
        quantity: option.quantity,
        unit: option.unit,
        amount: option.amount,
        price: option.price,
      }));
      const optionSummary = selectedOptions.map((option) => option.optionName).join(', ');

      const record: InvoiceNotification = {
        lineKey: `${stableLinePart(invoice.invoiceCode)}__${fingerprint}__${occurrence}`,
        stt: String(result.length + 1),
        invoiceCode: invoice.invoiceCode,
        billNumber: invoice.invoiceCode,
        checkInDate: invoice.saleDate || '',
        checkInTime: invoice.checkInTime || '',
        checkOutDate: invoice.saleDate || '',
        checkOutTime: invoice.checkOutTime || '',
        tableName: invoice.tableName || '',
        guestCount: invoice.guestCount,
        employeeName: invoice.serverName || invoice.cashierName || '',
        shiftName: invoice.shiftName || '',
        itemName: item.itemName,
        itemId: item.itemId,
        quantity: item.quantity,
        unit: item.unit || '',
        unitPrice: item.unitPrice,
        lineTotal: item.lineTotal,
        vatRate: item.vatRate ? Number(item.vatRate) : undefined,
        selectedOptions,
        optionSummary,
        orderCode: invoice.invoiceCode,
        customerName: invoice.customerName || '',
        saleDate: invoice.saleDate || '',
        totalAmount: invoice.totalAmount,
        currency: 'VND',
        channel: invoice.serviceMode || invoice.area || '',
        notificationTitle: `Hóa đơn ${invoice.invoiceCode} - ${item.itemName}`,
        notificationContent: '',
        status: 'paid',
        isActive: true,
        rawData: {
          source: 'cukcuk',
          invoiceCode: invoice.invoiceCode,
          itemGroup: item.itemGroup,
          lineIndex: item.sourceRowNumber,
          mappedOptions: item.selectedOptions.map((option) => ({
            optionName: option.optionName,
            mappedCode: option.mappedOption?.code ?? null,
            matchScore: option.matchScore ?? 0,
            matchMethod: option.matchMethod ?? 'none',
          })),
        },
      };

      record.notificationContent = buildNotificationContent(record);
      result.push(record);
    });
  });

  return result;
};

export const parseCukcukInvoicesWithOptionMappingFromRows = (
  rows: unknown[][],
  sheetName: string,
  fileName: string,
  sheetNames: string[],
  existingOptions: MenuOption[],
): ParseCukcukInvoicesWithMappingResult => {
  const { headerRowIndex, dataStartRowIndex } = resolveCukcukInvoiceDataBounds(rows);
  const invoiceGroups = groupInvoiceRowsByCode(rows, dataStartRowIndex);

  const warnings: string[] = [];
  const invoices: MappedCukcukInvoice[] = [];
  let totalLineItems = 0;
  let totalOptions = 0;

  invoiceGroups.forEach((group) => {
    const mapped = mapInvoiceOptions(group.rows, existingOptions);

    mapped.warnings.forEach((warning) => {
      warnings.push(`HĐ ${group.invoiceCode}: ${warning}`);
    });

    const invoice: MappedCukcukInvoice = {
      invoiceCode: group.invoiceCode,
      items: mapped.items,
    };

    if (group.summaryRow) {
      applyInvoiceSummary(invoice, group.summaryRow);
    }

    invoices.push(invoice);
    totalLineItems += mapped.items.length;
    totalOptions += mapped.totalOptions;
  });

  const meta: CukcukInvoiceParseMeta = {
    fileName,
    sheetNames,
    sheetName,
    parserVersion: PARSER_VERSION,
    warnings,
    headerRowIndex: headerRowIndex + 1,
    dataStartRowIndex: dataStartRowIndex + 1,
    totalInvoices: invoices.length,
    totalLineItems,
    totalOptions,
  };

  return { invoices, meta };
};

/**
 * Parse file chi tiết doanh thu CUKCUK, gom tùy chọn qua `mapInvoiceOptions`
 * và map sang catalog option hiện có.
 */
export const parseCukcukInvoicesWithOptionMapping = async (
  file: File,
  existingOptions: MenuOption[],
): Promise<ParseCukcukInvoicesWithMappingResult> => {
  const workbookData = await readCukcukWorkbookData(file);
  const sheetNames = workbookData.sheetNames;

  if (!sheetNames.length) {
    throw new Error('File Excel không có sheet nào.');
  }

  const sheetName = pickSheetName(sheetNames, INVOICE_SHEET_HINTS, 0);
  if (!sheetName) {
    throw new Error('Không tìm thấy sheet dữ liệu hóa đơn trong file CUKCUK.');
  }

  const sheet = workbookData.sheets[sheetName];
  if (!sheet) {
    throw new Error(`Không đọc được sheet: ${sheetName}`);
  }

  return parseCukcukInvoicesWithOptionMappingFromRows(
    getSheetMatrix(sheet),
    sheetName,
    file.name,
    sheetNames,
    existingOptions,
  );
};

export interface CukcukInvoiceImportResult {
  notifications: InvoiceNotification[];
  warnings: string[];
  meta: CukcukInvoiceParseMeta;
}

/** Luồng import đầy đủ: nhận diện CUKCUK → map option → chuyển sang notification. */
export const parseCukcukInvoiceFileToNotifications = async (
  file: File,
  sources: Customization[] | OptionCatalogSources,
): Promise<CukcukInvoiceImportResult> => {
  const catalog = Array.isArray(sources)
    ? buildMenuOptionCatalogFromCustomizations(sources)
    : resolveOptionCatalog(sources);

  const parsed = await parseCukcukInvoicesWithOptionMapping(file, catalog);
  const warnings = [...parsed.meta.warnings];

  if (!catalog.length) {
    warnings.unshift(
      'Catalog option trống — tùy chọn vẫn được gom nhưng dùng mã sinh tự động, chưa map sang hệ thống.',
    );
  }

  return {
    notifications: cukcukInvoicesToNotifications(parsed.invoices),
    warnings,
    meta: { ...parsed.meta, warnings },
  };
};

export { isCukcukInvoiceFile };